// 🔁 Trading › Transacciones — operaciones (trades) y órdenes del bot.
// Lee trading.trades / trading.ordenes (SELECT de app_user). Columnas ordenables,
// filtros (par/entorno/motivo/rango de fecha), total de PnL al pie y export a XLS.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftRight, Loader2, FileSpreadsheet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { fechaLocal } from '@/lib/utils/fecha';
import { LOCALE } from '@/lib/utils/regional';

const TR = { headers: { 'Accept-Profile': 'trading', 'Content-Profile': 'trading' } };
const inputCls = 'mt-1 h-9 rounded-md border border-input bg-background px-2 text-sm';
const money = (v: number | null | undefined) => {
  const n = Number(v);
  if (v === null || v === undefined || Number.isNaN(n)) return '—';
  try { return new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'USD' }).format(n); }
  catch { return `$${n.toFixed(2)}`; }
};
const num = (v: number | null | undefined, d = 8) => (v === null || v === undefined || Number.isNaN(Number(v)) ? '—' : Number(v).toFixed(d).replace(/\.?0+$/, ''));
const pnlCls = (v: number | null | undefined) => (Number(v) > 0 ? 'text-green-600' : Number(v) < 0 ? 'text-red-600' : '');
const fecha = (s: string | null) => (s ? new Date(s).toLocaleString(LOCALE) : '—');

interface Trade {
  id: string; symbol: string; lado: string; cantidad: number; precio_entrada: number | null;
  precio_salida: number | null; pnl: number | null; pnl_pct: number | null; motivo_cierre: string | null;
  entorno: string; ts_apertura: string | null; ts_cierre: string | null;
}
interface Orden {
  id: string; client_order_id: string; symbol: string; lado: string; tipo: string;
  cantidad: number | null; precio: number | null; estado: string | null; filled: number | null;
  entorno: string; created_at: string;
}

type Tab = 'trades' | 'ordenes';

export function TradingTransacciones() {
  const [tab, setTab] = useState<Tab>('trades');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [par, setPar] = useState('');
  const [entorno, setEntorno] = useState('');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');

  const { data: trades = [], isFetching: fTrades } = useQuery<Trade[]>({
    queryKey: ['trading-trades'],
    queryFn: async () => (await apiClient.get('/trades', { ...TR, params: { order: 'ts_cierre.desc.nullslast', limit: 1000 } })).data || [],
    refetchInterval: 20000,
  });
  const { data: ordenes = [], isFetching: fOrd } = useQuery<Orden[]>({
    queryKey: ['trading-ordenes'],
    queryFn: async () => (await apiClient.get('/ordenes', { ...TR, params: { order: 'created_at.desc', limit: 1000 } })).data || [],
    refetchInterval: 20000,
  });

  const paresOpts = useMemo(() => ([...new Set([...trades, ...ordenes].map((x) => x.symbol))].sort()), [trades, ordenes]);
  const isFetching = tab === 'trades' ? fTrades : fOrd;

  // --- Operaciones (trades) ------------------------------------------------
  const tradesFiltrados = useMemo(() => {
    const arr = trades.filter((t) => {
      const f = (t.ts_cierre || t.ts_apertura || '').slice(0, 10);
      return (!desde || f >= desde) && (!hasta || f <= hasta) && (!par || t.symbol === par) && (!entorno || t.entorno === entorno);
    });
    const s = dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => String(a.ts_cierre || a.ts_apertura || '').localeCompare(String(b.ts_cierre || b.ts_apertura || '')) * s);
    return arr;
  }, [trades, desde, hasta, par, entorno, dir]);

  const totalPnl = useMemo(() => tradesFiltrados.reduce((acc, t) => acc + (Number(t.pnl) || 0), 0), [tradesFiltrados]);

  // --- Órdenes -------------------------------------------------------------
  const ordenesFiltradas = useMemo(() => {
    const arr = ordenes.filter((o) => {
      const f = (o.created_at || '').slice(0, 10);
      return (!desde || f >= desde) && (!hasta || f <= hasta) && (!par || o.symbol === par) && (!entorno || o.entorno === entorno);
    });
    const s = dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)) * s);
    return arr;
  }, [ordenes, desde, hasta, par, entorno, dir]);

  const exportarXls = async () => {
    const XLSX = await import('xlsx');
    const datos = tab === 'trades'
      ? tradesFiltrados.map((t) => ({
          Apertura: fecha(t.ts_apertura), Cierre: fecha(t.ts_cierre), Par: t.symbol, Lado: t.lado,
          Cantidad: Number(t.cantidad) || 0, 'Precio entrada': Number(t.precio_entrada) || 0,
          'Precio salida': t.precio_salida == null ? '' : Number(t.precio_salida), PnL: t.pnl == null ? '' : Number(t.pnl),
          'PnL %': t.pnl_pct == null ? '' : Number(t.pnl_pct), Motivo: t.motivo_cierre || '', Entorno: t.entorno,
        }))
      : ordenesFiltradas.map((o) => ({
          Fecha: fecha(o.created_at), Par: o.symbol, Lado: o.lado, Tipo: o.tipo,
          Cantidad: Number(o.cantidad) || 0, Precio: o.precio == null ? '' : Number(o.precio),
          Estado: o.estado || '', Filled: Number(o.filled) || 0, Entorno: o.entorno, 'Client Order ID': o.client_order_id,
        }));
    const ws = XLSX.utils.json_to_sheet(datos);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tab === 'trades' ? 'Operaciones' : 'Órdenes');
    XLSX.writeFile(wb, `trading_${tab}_${fechaLocal()}.xlsx`);
  };

  const filas = tab === 'trades' ? tradesFiltrados.length : ordenesFiltradas.length;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-3">
        <ArrowLeftRight className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Transacciones</h1>
          <p className="text-sm text-muted-foreground">Operaciones y órdenes ejecutadas por el bot.</p>
        </div>
      </div>

      <Card>
        <CardHeader className="space-y-3 pb-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-1 rounded-md border p-0.5 text-sm">
              <button type="button" onClick={() => setTab('trades')} className={`rounded px-3 py-1 ${tab === 'trades' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>Operaciones</button>
              <button type="button" onClick={() => setTab('ordenes')} className={`rounded px-3 py-1 ${tab === 'ordenes' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>Órdenes</button>
            </div>
            <CardTitle className="text-base">{tab === 'trades' ? 'Operaciones' : 'Órdenes'} ({filas})</CardTitle>
            <Button size="sm" variant="outline" onClick={exportarXls} disabled={filas === 0}>
              <FileSpreadsheet className="mr-1 h-4 w-4" /> Exportar XLS
            </Button>
          </div>
          <div className="flex flex-wrap items-end gap-2 text-xs text-muted-foreground">
            <label className="flex flex-col">Desde<input type="date" className={inputCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
            <label className="flex flex-col">Hasta<input type="date" className={inputCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
            <label className="flex flex-col">Par
              <select className={`${inputCls} w-40`} value={par} onChange={(e) => setPar(e.target.value)}>
                <option value="">Todos</option>
                {paresOpts.map((p) => <option key={p} value={p}>{p}</option>)}
              </select></label>
            <label className="flex flex-col">Entorno
              <select className={`${inputCls} w-36`} value={entorno} onChange={(e) => setEntorno(e.target.value)}>
                <option value="">Todos</option><option value="testnet">testnet</option><option value="produccion">produccion</option>
              </select></label>
            <label className="flex flex-col">Orden por fecha
              <select className={`${inputCls} w-36`} value={dir} onChange={(e) => setDir(e.target.value as 'asc' | 'desc')}>
                <option value="desc">Más reciente</option><option value="asc">Más antigua</option>
              </select></label>
            {(desde || hasta || par || entorno) && (
              <Button size="sm" variant="outline" onClick={() => { setDesde(''); setHasta(''); setPar(''); setEntorno(''); }}>Limpiar</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>}

          {tab === 'trades' ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">Apertura</th><th className="py-2 pr-3">Cierre</th><th className="py-2 pr-3">Par</th>
                  <th className="py-2 pr-3">Lado</th><th className="py-2 pr-3 text-right">Cantidad</th>
                  <th className="py-2 pr-3 text-right">Entrada</th><th className="py-2 pr-3 text-right">Salida</th>
                  <th className="py-2 pr-3 text-right">PnL</th><th className="py-2 pr-3 text-right">%</th>
                  <th className="py-2 pr-3">Motivo</th><th className="py-2 pr-3">Entorno</th>
                </tr>
              </thead>
              <tbody>
                {tradesFiltrados.length === 0 && !isFetching && <tr><td colSpan={11} className="py-8 text-center text-muted-foreground">Sin operaciones.</td></tr>}
                {tradesFiltrados.map((t) => (
                  <tr key={t.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 whitespace-nowrap text-xs">{fecha(t.ts_apertura)}</td>
                    <td className="py-2 pr-3 whitespace-nowrap text-xs">{t.ts_cierre ? fecha(t.ts_cierre) : <span className="text-primary">abierta</span>}</td>
                    <td className="py-2 pr-3">{t.symbol}</td>
                    <td className="py-2 pr-3 uppercase">{t.lado}</td>
                    <td className="py-2 pr-3 text-right font-mono text-xs">{num(t.cantidad)}</td>
                    <td className="py-2 pr-3 text-right font-mono text-xs">{num(t.precio_entrada, 2)}</td>
                    <td className="py-2 pr-3 text-right font-mono text-xs">{num(t.precio_salida, 2)}</td>
                    <td className={`py-2 pr-3 text-right whitespace-nowrap font-medium ${pnlCls(t.pnl)}`}>{t.pnl == null ? '—' : money(t.pnl)}</td>
                    <td className={`py-2 pr-3 text-right ${pnlCls(t.pnl)}`}>{t.pnl_pct == null ? '—' : `${Number(t.pnl_pct).toFixed(2)}%`}</td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">{t.motivo_cierre || '—'}</td>
                    <td className="py-2 pr-3 text-xs uppercase">{t.entorno}</td>
                  </tr>
                ))}
              </tbody>
              {tradesFiltrados.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 font-semibold">
                    <td className="py-2 pr-3" colSpan={7}>Total PnL ({tradesFiltrados.length})</td>
                    <td className={`py-2 pr-3 text-right whitespace-nowrap ${pnlCls(totalPnl)}`}>{money(totalPnl)}</td>
                    <td className="py-2 pr-3" colSpan={3}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Par</th><th className="py-2 pr-3">Lado</th>
                  <th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3 text-right">Cantidad</th>
                  <th className="py-2 pr-3 text-right">Precio</th><th className="py-2 pr-3 text-right">Filled</th>
                  <th className="py-2 pr-3">Estado</th><th className="py-2 pr-3">Entorno</th><th className="py-2 pr-3">Client Order ID</th>
                </tr>
              </thead>
              <tbody>
                {ordenesFiltradas.length === 0 && !isFetching && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin órdenes.</td></tr>}
                {ordenesFiltradas.map((o) => (
                  <tr key={o.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 whitespace-nowrap text-xs">{fecha(o.created_at)}</td>
                    <td className="py-2 pr-3">{o.symbol}</td>
                    <td className="py-2 pr-3 uppercase">{o.lado}</td>
                    <td className="py-2 pr-3">{o.tipo}</td>
                    <td className="py-2 pr-3 text-right font-mono text-xs">{num(o.cantidad)}</td>
                    <td className="py-2 pr-3 text-right font-mono text-xs">{num(o.precio, 2)}</td>
                    <td className="py-2 pr-3 text-right font-mono text-xs">{num(o.filled)}</td>
                    <td className="py-2 pr-3 text-xs">{o.estado || '—'}</td>
                    <td className="py-2 pr-3 text-xs uppercase">{o.entorno}</td>
                    <td className="py-2 pr-3 font-mono text-[11px] text-muted-foreground"><span className="inline-block max-w-52 truncate align-middle" title={o.client_order_id}>{o.client_order_id}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
